import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import { downloadOutput } from "../../actions/files/downloadOutput";
import { TextTransformType } from "../../actions/text-transform/createTextTransform";
import { ChatInput } from "../chat/ChatInput";
import { useExtractChat } from "../../hooks/useExtractChat";
import CustomPromptButtons from "./CustomPromptButtons";
import { TRANSFORM_ACTIONS, TransformPanelUiAction } from "./transformConfig";
import AnimatedStreamingText from "../common/AnimatedStreamingText";

interface TransformPanelProps {
  displayText: string;
  hasText: boolean;
  isTransforming: boolean;
  isStreamingResult?: boolean;
  isSourceStreaming?: boolean;
  transformError?: string;
  downloadUrl?: string;
  inlineChatJobId?: string;
  inlineChatFileName?: string;
  onTransform: (transformType: TextTransformType, customInstruction?: string) => Promise<void> | void;
  onReset: () => void;
}

const copyToClipboard = async (text: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "true");
  textArea.style.position = "absolute";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
};

const buildMailtoLink = (text: string) => {
  let subject = "AI-SCRIBE Output";
  let body = text;

  const lines = text.trim().split("\n");
  for (const line of lines) {
    if (line.trim().toLowerCase().startsWith("subject:")) {
      subject = line.trim().slice(8).trim() || subject;
      body = lines.slice(1).join("\n").trim();
      break;
    }
  }

  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const TransformPanel: React.FC<TransformPanelProps> = ({
  displayText,
  hasText,
  isTransforming,
  isStreamingResult = false,
  isSourceStreaming = false,
  transformError,
  downloadUrl,
  inlineChatJobId,
  inlineChatFileName,
  onTransform,
  onReset,
}) => {
  const [showCustom, setShowCustom] = useState(false);
  const [customInstruction, setCustomInstruction] = useState("");
  const [copyMessage, setCopyMessage] = useState("");
  const [downloadMessage, setDownloadMessage] = useState("");
  const customTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const {
    messages: inlineChatMessages,
    isLoading: inlineChatLoading,
    error: inlineChatError,
    sendMessage: sendInlineChatMessage,
    clearMessages: clearInlineChatMessages,
  } = useExtractChat({ jobId: inlineChatJobId || "", extractedText: displayText });

  const mailtoLink = useMemo(() => buildMailtoLink(displayText), [displayText]);
  const isGeneratingContent = isTransforming || isStreamingResult || isSourceStreaming;

  useEffect(() => {
    if (!showCustom) {
      return;
    }

    window.setTimeout(() => {
      customTextareaRef.current?.focus();
      customTextareaRef.current?.select();
    }, 0);
  }, [showCustom]);

  if (!hasText) {
    return null;
  }

  const handleTransformClick = async (type: TextTransformType) => {
    setCopyMessage("");
    await onTransform(type);
  };

  const handleCustomSubmit = async () => {
    const trimmedInstruction = customInstruction.trim();
    if (!trimmedInstruction) {
      return;
    }

    setCopyMessage("");
    await onTransform("custom", trimmedInstruction);
    setShowCustom(false);
  };

  const handleCopy = async () => {
    try {
      await copyToClipboard(displayText);
      setCopyMessage("Copied");
      window.setTimeout(() => setCopyMessage(""), 1800);
    } catch (error) {
      console.error("Failed to copy transformed text:", error);
      setCopyMessage("Copy failed");
      window.setTimeout(() => setCopyMessage(""), 1800);
    }
  };

  const handleDownload = async () => {
    if (!downloadUrl) {
      return;
    }

    try {
      await downloadOutput(downloadUrl);
      setDownloadMessage("Downloaded");
      window.setTimeout(() => setDownloadMessage(""), 1800);
    } catch (error) {
      console.error("Failed to download output:", error);
      setDownloadMessage("Download failed");
      window.setTimeout(() => setDownloadMessage(""), 1800);
    }
  };

  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography
        sx={{
          fontFamily: "'Syne', sans-serif",
          fontSize: "0.68rem",
          fontWeight: 600,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "var(--t4)",
          mb: 1,
        }}
      >
        Transform
      </Typography>

      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2 }}>
        {TRANSFORM_ACTIONS.map((action) => (
          <Button
            key={action.type}
            variant="outlined"
            size="small"
            disabled={isTransforming}
            onClick={() => {
              if (action.type === TransformPanelUiAction.RESET) {
                setShowCustom(false);
                onReset();
                return;
              }

              if (action.type === TransformPanelUiAction.CUSTOM_TOGGLE) {
                if (showCustom) {
                  customTextareaRef.current?.focus();
                } else {
                  setShowCustom(true);
                }
                return;
              }

              void handleTransformClick(action.type);
            }}
            sx={{
              flex: "1 1 auto",
              minWidth: 0,
              fontSize: "0.78rem",
              fontWeight: 500,
              py: 0.75,
            }}
          >
            {action.icon} {action.label}
          </Button>
        ))}
      </Box>

      {showCustom && (
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "var(--t4)",
              mb: 0.5,
            }}
          >
            Custom instruction
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: "rgba(226, 232, 240, 0.72)",
              mb: 0.75,
            }}
          >
            Use `Custom` for a one-off instruction. `Improve` runs immediately.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <textarea
                ref={customTextareaRef}
                value={customInstruction}
                onChange={(event) => setCustomInstruction(event.target.value)}
                placeholder='e.g. "List all action items"'
                rows={2}
                style={{
                  width: "100%",
                  minHeight: "44px",
                  background: "rgba(15, 23, 42, 0.58)",
                  color: "#e2e8f0",
                  border: "1px solid rgba(148, 163, 184, 0.18)",
                  borderRadius: "12px",
                  padding: "10px 12px",
                  resize: "vertical",
                  font: "inherit",
                }}
              />
            </Box>
            <Button variant="contained" size="small" onClick={() => void handleCustomSubmit()} disabled={isTransforming}>
              Run →
            </Button>
          </Box>
        </Box>
      )}

      <CustomPromptButtons
        disabled={!hasText}
        isBusy={isTransforming}
        onRunPrompt={(prompt) => onTransform("custom", prompt)}
      />

      {transformError && (
        <Alert severity="error" sx={{ mt: 1.5, borderRadius: "14px" }}>
          {transformError}
        </Alert>
      )}

      <Box
        sx={{
          mt: 2,
          position: "relative",
          background: "rgba(8, 15, 24, 0.72)",
          backdropFilter: "blur(18px)",
          border: "1px solid rgba(148, 163, 184, 0.16)",
          borderRadius: "4px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            alignItems: "center",
            p: 1.5,
            borderBottom: "1px solid rgba(148, 163, 184, 0.12)",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Syne', sans-serif",
              fontSize: "0.78rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "rgba(226, 232, 240, 0.92)",
            }}
          >
            Extracted Content
          </Typography>
          {isGeneratingContent && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1,
                py: 0.45,
                borderRadius: "999px",
                border: "1px solid rgba(44, 192, 183, 0.2)",
                background: "rgba(44, 192, 183, 0.08)",
                color: "rgba(153, 246, 228, 0.96)",
              }}
            >
              <CircularProgress size={12} thickness={6} sx={{ color: "var(--teal)" }} />
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 600 }}>
                {(isStreamingResult || isSourceStreaming) ? "Streaming response" : "Generating response"}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {downloadUrl && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => void handleDownload()}
                disabled={isGeneratingContent}
              >
                {downloadMessage || "Download"}
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={() => void handleCopy()}
              disabled={isGeneratingContent}
            >
              {copyMessage || "Copy"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              component="a"
              href={isGeneratingContent ? undefined : mailtoLink}
              disabled={isGeneratingContent}
              sx={isGeneratingContent ? { pointerEvents: "none" } : undefined}
            >
              Email
            </Button>
          </Box>
        </Box>

        <AnimatedStreamingText
          text={displayText}
          isStreaming={isTransforming || isStreamingResult || isSourceStreaming}
          px={2}
          py={2.25}
          maxHeight={420}
          fontSize="0.96rem"
          lineHeight={1.75}
        />
      </Box>

      {inlineChatJobId && (
        <Box sx={{ mt: 2 }}>
          <Typography
            sx={{
              fontFamily: "'Syne', sans-serif",
              fontSize: "0.68rem",
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--t4)",
              mb: 1,
            }}
          >
            Ask Questions About The Extracted Content
          </Typography>

          <Paper
            elevation={0}
            sx={{
              borderRadius: "4px",
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(9, 18, 28, 0.82)",
              backdropFilter: "blur(18px)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.8)" }}>
                {inlineChatFileName || "Document"} chat is grounded in the extracted job output.
              </Typography>
              {inlineChatMessages.length > 0 && (
                <Button variant="outlined" size="small" onClick={clearInlineChatMessages} disabled={inlineChatLoading}>
                  Clear Chat
                </Button>
              )}
            </Box>

            <Box
              sx={{
                px: 2,
                py: 2,
                maxHeight: 320,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              {inlineChatMessages.length === 0 && (
                <Typography sx={{ color: "rgba(255,255,255,0.64)", lineHeight: 1.7 }}>
                  Ask about clauses, parties, obligations, dates, or whether a phrase appears in the extracted content.
                </Typography>
              )}

              {inlineChatMessages.map((message) => {
                const isUser = message.role === "user";
                return (
                  <Box
                    key={message.id}
                    sx={{
                      alignSelf: isUser ? "flex-end" : "flex-start",
                      maxWidth: "85%",
                      px: 1.75,
                      py: 1.25,
                      borderRadius: 2.5,
                      bgcolor: isUser ? "rgba(44, 192, 183, 0.18)" : "rgba(255,255,255,0.07)",
                      border: isUser
                        ? "1px solid rgba(44, 192, 183, 0.35)"
                        : "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <Typography sx={{ color: "#f7fbff", whiteSpace: "pre-wrap", lineHeight: 1.7, fontSize: "0.92rem" }}>
                      {message.content}
                    </Typography>
                  </Box>
                );
              })}

              {inlineChatLoading && (
                <Box
                  sx={{
                    alignSelf: "flex-start",
                    px: 1.75,
                    py: 1.25,
                    borderRadius: 2.5,
                    bgcolor: "rgba(255,255,255,0.07)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress size={14} thickness={5} sx={{ color: "var(--teal)" }} />
                  <Typography sx={{ color: "#f7fbff", fontSize: "0.9rem" }}>
                    Answering from extracted content...
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              {inlineChatError && (
                <Alert severity="error" sx={{ mb: 1.5, borderRadius: "14px" }}>
                  {inlineChatError}
                </Alert>
              )}
              <ChatInput
                onSend={sendInlineChatMessage}
                disabled={inlineChatLoading || isGeneratingContent}
                placeholder={
                  isGeneratingContent
                    ? "Wait for the response to finish before asking…"
                    : "Ask a question about this content..."
                }
                maxLength={2000}
                borderRadius="4px"
              />
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default TransformPanel;
